import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sessionId = params.get("session_id");
  const isPayg = params.get("payg") === "1";
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [plan, setPlan] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 6) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setPlan(r.data.plan_name || "");
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  const dash = user?.role === "employer" ? "/employer" : "/candidate";

  return (
    <main data-testid="billing-success-page" className="max-w-xl mx-auto px-5 py-24">
      <div className="border border-[#0A0A0A] p-10 text-center">
        {state === "checking" && (
          <div className="flex flex-col items-center gap-4" data-testid="billing-checking">
            <Loader2 size={40} className="animate-spin text-[#002FA7]" />
            <h1 className="font-display font-bold text-2xl">Confirming your payment…</h1>
            <p className="text-sm text-[#4B5563]">Please don't close this window.</p>
          </div>
        )}
        {state === "paid" && (
          <div className="flex flex-col items-center gap-4" data-testid="billing-paid">
            <CheckCircle2 size={48} className="text-[#10B981]" />
            <h1 className="font-display font-black text-3xl tracking-tighter">{isPayg ? "Posting access added!" : "You're subscribed!"}</h1>
            <p className="text-[#4B5563]">{isPayg ? `${plan || "Your posting pass"} is active — you can now publish your job.` : plan ? `${plan} is now active on your account.` : "Your plan is now active."}</p>
            <Link to={isPayg ? "/employer" : dash} className="btn-ink px-6 py-3 font-medium mt-2">{isPayg ? "Post a job" : "Go to dashboard"}</Link>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div className="flex flex-col items-center gap-4" data-testid="billing-failed">
            <XCircle size={48} className="text-[#FF3B30]" />
            <h1 className="font-display font-bold text-2xl">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-sm text-[#4B5563]">
              {state === "timeout"
                ? "We're still confirming your payment. Check your dashboard shortly."
                : "Your payment was cancelled or could not be verified."}
            </p>
            <Link to="/pricing" className="btn-outline px-6 py-3 font-medium mt-2">Back to pricing</Link>
          </div>
        )}
      </div>
    </main>
  );
}
